import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RequireMatch } from '@shared/validators/app.validators';
import { TypeDto } from '@models/common/TypeDto';
import { Component, Inject, Type, OnInit, ViewChild, ComponentRef, ViewContainerRef, OnDestroy, AfterViewInit, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators, ValidatorFn, FormGroup, FormBuilder } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { AppCheckboxComponent } from "../checkbox/checkbox.component";

export interface ModalButton {
  label: string;
  callback: (inputValue?: any) => void;
  color?: 'primary' | 'accent' | 'warn';
  keepModalOpen?: boolean;
}

export interface ModalData {
  title: string;
  message: string;
  messageParams?: any;
  buttons: ModalButton[];
  panelClass?: string;
  componentName?: Type<any>;
  componentData?: any;
  componentEvents?: (event: string, data: any) => void;
  closeAfterEvents?: boolean;
  inputValue?: string;
  showInput?: boolean;
  inputValidators?: ValidatorFn[];
  allowedInput?: string[];
  allowedInputMode?: string | 'autocomplete' | 'checkbox';
  inputPlaceholder?: string;
  hideDialogActions?: boolean;
  hideAlertIcon?: boolean;
  hideCloseButton?: boolean;
  showSpinner?: boolean;
  disableClose?: boolean;
}

@Component({
  selector: 'app-modal-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    AutocompleteComponent,
    AppCheckboxComponent
],
  templateUrl: './modal-dialog.component.html',
  styleUrl: './modal-dialog.component.css',
})
export class ModalDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('childContainer', { static: false }) childContainer: ViewContainerRef | undefined;
  private eventSubscriptions: Subscription[] = []; 
  filteredOptions!: Observable<string[]>;

  modalForm: FormGroup = new FormGroup({});
  requireMatch: boolean = false;

  selectedValues: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalData,
    private dialogRef: MatDialogRef<ModalDialogComponent>,
    private translateService: TranslateService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    if (this.data.showInput) {
      // simple input
      if (!this.data.inputValidators) {
        this.data.inputValidators = [Validators.required];
      } else {
        this.requireMatch = this.data.inputValidators.some(v => (v as any)._validatorId === 'RequireMatch');
      }
      if (!this.data.allowedInputMode) {
        this.modalForm = new FormGroup({
          input: new FormControl(this.data.inputValue, this.data.inputValidators),
        });
      } else if (this.data.allowedInput && this.data.allowedInputMode == 'checkbox') {
        this.modalForm = this.fb.group({
          // Il FormArray che conterrà un FormControl per ogni checkbox
          selections: this.fb.array(this.data.allowedInput.map(item => new FormControl(false)))
        });
      }
    }
    // input or auto complete
    this.data.showInput = this.data.showInput === true || !!this.data.allowedInput;
    /*
    this.filteredOptions = this.autoCompleteControl.valueChanges.pipe(
      startWith(''),
      map((value: string | null) => {
        this.inputValue = value || '';
        if (this.data.allowedInput) {
          return value ? this._filter(value) : this.data.allowedInput.slice();
        } else {
          return [];
        }
      }),
    );
    */
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.childContainer && this.data.componentEvents) 
        this.subscribeToDynamicComponentOutputs();  
    });
  }

  ngOnDestroy() {
    this.eventSubscriptions.forEach(sub => sub.unsubscribe());
  }

  private _filter(name: string): string[] {
    const filterValue = name.toLowerCase();
    if (!this.data.allowedInput) {
      return [];
    }
    return this.data.allowedInput.filter((option) =>
      this.translateService.instant(option).toLowerCase().includes(filterValue),
    );
  }

  displayFn = (value: string): string => {
    return value ? this.translateService.instant(value) : '';
  };

  inputValue: string = '';
  onOptionSelected(value: any) {
    this.inputValue = value || '';
  }

  onClick(btn: ModalButton): void {
    let dataBack: any;
    if (this.data.showInput) {
      if (this.data.allowedInput && this.data.allowedInputMode == 'checkbox') {
        const selections = this.modalForm.get('selections')?.value;
        dataBack = this.data.allowedInput?.filter((option, index) => selections[index]);
      } else {
        dataBack = this.inputValue || this.modalForm.get('input')?.value;
      }
    } else {
      dataBack = this.data.componentData;
    }
    btn.callback(dataBack);
    if (!btn.keepModalOpen) {
      this.close();
    }
  }

  private subscribeToDynamicComponentOutputs() {
    const componentInstance = (this.childContainer as any)._componentRef?.instance;

    if (componentInstance) {
      for (const propName in componentInstance) {
        if (componentInstance.hasOwnProperty(propName)) {
          const property = (componentInstance as any)[propName];
          // Verifica se la proprietà è un EventEmitter (tipicamente un @Output)
          if (property && typeof property.subscribe === 'function' && typeof property.emit === 'function') {
            const sub = property.subscribe((eventData: any) => {
              //const receivedEventLog = `Evento da ${componentInstance.constructor.name} - Prop: ${propName} - Dati: ${JSON.stringify(eventData)}`;
              //console.log(receivedEventLog);
              this.data.componentEvents!(propName, eventData);
              if (this.data.closeAfterEvents) {
                this.close();
              }
            });
            this.eventSubscriptions.push(sub); // Aggiungi la sottoscrizione per gestirla in OnDestroy
          }
        }
      }
    } else {
        console.warn('Istanza del componente dinamico non ancora disponibile.');
    }
  }

  close() {
    this.dialogRef.close();
  }
}
